import { ProductTypeEnum, type ProductDeposit } from "@kanpla/api-contract";

import { type Product } from "~/api/products/types";

import { type BasketItemVariant } from "../../types";

/**
 * Weighted products carry no deposit: a per-unit fee has no unit once the
 * product is sold by weight (DRS ADR 5.16).
 *
 * The basket charges the fee and the order payload declares it, and the server
 * rejects an order whose declared deposit differs from its own resolution — so
 * both read it from here instead of each applying the rule.
 */
export function resolveLineDeposit(product: Product): ProductDeposit | null {
  if (product.type === ProductTypeEnum.WEIGHTED) return null;

  return product.deposit;
}

/**
 * The deposit of each picked choice, by choice id, read from the catalog like
 * the product's own so a basket restored from storage charges today's fee.
 * Charged on top of the product's deposit (ADR-0031), per unit of the choice.
 */
export function resolveVariantDeposits(
  product: Product,
  choices: BasketItemVariant["choices"]
): Record<string, ProductDeposit> {
  if (product.type !== ProductTypeEnum.VARIANTS) return {};

  return Object.fromEntries(
    choices.flatMap((choice) => {
      if (choice.quantity <= 0) return [];

      // Looked up under its own option, as the choice's price is. The result is
      // keyed by choice id like the display choices, so two options that share
      // a choice id collapse here the same way they already do for prices.
      const deposit = product.options
        .find((option) => option.id === choice.variantId)
        ?.choices.find((candidate) => candidate.id === choice.id)?.deposit;

      return deposit ? [[choice.id, deposit] as const] : [];
    })
  );
}
